import { GraphMode, ItemType } from '../../constants';
import { NodeModel, RemoveCommandParams, Node, NodeMap } from '../../types';
import {
  canAnyNodeBeDeleted,
  canDeleteNode,
  executeBatch,
  getDeletableNodes,
  getGlobal,
} from '../../utils';
import { BaseCommand, baseCommand } from '../base';

const getDeletableNodeIdsFromMap = (nodeMap: NodeMap) => {
  return Object.entries(nodeMap).reduce((acc: string[], [id, model]) => {
    return canDeleteNode(model) ? [...acc, id] : acc;
  }, []);
};

export const removeCommand: BaseCommand<RemoveCommandParams> = {
  ...baseCommand,

  params: {
    flow: {
      nodes: {},
      edges: {},
    },
  },

  canExecute(graph) {
    if (
      getGlobal(graph).plugin.inlineEditInput.state === 'show' ||
      graph.getCurrentMode() === GraphMode.Readonly
    )
      return false;

    const selectedNodes: Node[] = this.getSelectedNodes(graph) || [];
    const selectedEdges = this.getSelectedEdges(graph) || [];
    const hasNodes = !!selectedNodes.length;
    const hasEdges = !!selectedEdges.length;

    if (!hasEdges) {
      return canAnyNodeBeDeleted(selectedNodes);
    }

    return hasNodes || hasEdges;
  },

  init(graph) {
    const selectedNodes = getDeletableNodes(this.getSelectedNodes(graph)) as Node[];
    const selectedEdges = this.getSelectedEdges(graph);

    /**
     * If we begin to support TreeGraphs, we need to add additional logic
     * See line 46 of the following:
     * https://github.com/alibaba/GGEditor/blob/master/src/components/Graph/command/remove.ts
     */
    const { nodes, edges } = this.params.flow;

    selectedNodes.forEach((node) => {
      const nodeModel = node.getModel() as NodeModel;
      const nodeEdges = node.getEdges();

      nodes[nodeModel.id] = nodeModel;

      nodeEdges.forEach((edge) => {
        const edgeModel = edge.getModel();

        if (edgeModel.id) {
          edges[edgeModel.id] = edgeModel;
        }
      });
    });

    selectedEdges.forEach((edge) => {
      const edgeModel = edge.getModel();

      if (edgeModel.id) {
        edges[edgeModel.id] = edgeModel;
      }
    });
  },

  execute(graph) {
    /**
     * If we begin to support TreeGraphs, we need to add additional logic
     * See line 82 of the following:
     * https://github.com/alibaba/GGEditor/blob/master/src/components/Graph/command/remove.ts
     */
    const { nodes, edges } = this.params.flow;

    executeBatch(graph, () => {
      [...getDeletableNodeIdsFromMap(nodes), ...Object.keys(edges)].forEach((id) => {
        graph.removeItem(id);
      });
    });
  },

  undo(graph) {
    /**
     * If we begin to support TreeGraphs, we need to add additional logic
     * See line 102 of the following:
     * https://github.com/alibaba/GGEditor/blob/master/src/components/Graph/command/remove.ts
     */
    const { nodes, edges } = this.params.flow;

    executeBatch(graph, () => {
      Object.keys(nodes).forEach((id) => {
        const model = nodes[id];

        graph.addItem(ItemType.Node, model);
      });

      Object.keys(edges).forEach((id) => {
        const model = edges[id];

        graph.addItem(ItemType.Edge, model);
      });
    });
  },

  shortcuts: ['Delete', 'Backspace'],
};

export default removeCommand;
